import { employmentTypeLabel, } from "../model";
import { formatDate, formatEmployedInterval, formatNumber, round } from "../utils";
export const employeeDisplayProperties = [
    // employee core data fields
    "fullName",
    "fullId",
    "lastName",
    "firstName",
    "timePin",
    "callName",
    "address",
    "city",
    "postalCode",
    "staffNumber",
    "email",
    "phone",
    "phone2",
    "notes",
    "birthday",
    "birthName",
    "birthCity",
    "birthCountry",
    "nationality",
    "gender",
    "taxId",
    "socialSecurityNumber",
    // contract fields
    "employmentType",
    "nominalWeeklyHours",
    "vacationDays",
    "monthlySalary",
    "hourlySalary",
    "contract_end",
    "probationEnd",
    "contract_start",
    "employment_date",
    "employedDays",
    "sfnAdvance",
    "bonuses",
    "benefits",
    // time report fields,
    "timeReportDays",
    "timeReportSundays",
    "timeReportHours",
    "vacationReportDays",
    "vacationReportHours",
    "sickReportDays",
    "sickReportHours",
    // time balance fields
    "timeBalanceCarry",
    "timeBalanceNominal",
    "timeBalanceActual",
    "timeBalanceDifference",
    "timeBalanceBalance",
    // vacation balance fields
    "vacationBalanceCarry",
    "vacationBalanceNominal",
    "vacationBalanceActual",
    "vacationBalanceDifference",
    "vacationBalanceBalance",
    // bonus balance fields
    "bonusesBalanceCarry",
    "bonusesBalanceNominal",
    "bonusesBalanceActual",
    "bonusesBalanceDifference",
    "bonusesBalanceBalance",
];
export const columnConfigs = {
    // Employee core date fields
    fullName: {
        getHeader: () => "Voller Name",
        getValue: ({ employee }) => `${employee.lastName}, ${employee.firstName}`,
        requiredPermissions: ["manage.employees.core_data"],
        requiredContext: ["employee"],
    },
    fullId: {
        getHeader: () => "Voller Name + Pers-Nr.",
        getValue: ({ employee }) => `${employee.lastName}, ${employee.firstName} #${employee.staffNumber?.toString() || ""}`,
        requiredPermissions: ["manage.employees.core_data"],
        requiredContext: ["employee"],
    },
    firstName: {
        getHeader: () => "Vorname",
        getValue: ({ employee }) => employee.firstName,
        requiredPermissions: ["manage.employees.core_data"],
        requiredContext: ["employee"],
    },
    lastName: {
        getHeader: () => "Nachname",
        getValue: ({ employee }) => employee.lastName,
        requiredPermissions: ["manage.employees.core_data"],
        requiredContext: ["employee"],
    },
    staffNumber: {
        getHeader: () => "Pers-Nr.",
        getValue: ({ employee }) => employee.staffNumber?.toString() || "",
        requiredPermissions: ["manage.employees.core_data"],
        requiredContext: ["employee"],
    },
    timePin: {
        getHeader: () => "Zeit-PIN",
        getValue: ({ employee }) => employee.timePin || "",
        requiredPermissions: ["manage.employees.core_data"],
        requiredContext: ["employee"],
    },
    callName: {
        getHeader: () => "Rufname",
        getValue: ({ employee }) => employee.callName || "",
        requiredPermissions: ["manage.employees.core_data"],
        requiredContext: ["employee"],
    },
    address: {
        getHeader: () => "Adresse",
        getValue: ({ employee }) => employee.address || "",
        requiredPermissions: ["manage.employees.core_data"],
        requiredContext: ["employee"],
    },
    city: {
        getHeader: () => "Stadt",
        getValue: ({ employee }) => employee.city || "",
        requiredPermissions: ["manage.employees.core_data"],
        requiredContext: ["employee"],
    },
    postalCode: {
        getHeader: () => "PLZ",
        getValue: ({ employee }) => employee.postalCode || "",
        requiredPermissions: ["manage.employees.core_data"],
        requiredContext: ["employee"],
    },
    email: {
        getHeader: () => "E-Mail",
        getValue: ({ employee }) => employee.email || "",
        requiredPermissions: ["manage.employees.core_data"],
        requiredContext: ["employee"],
    },
    phone: {
        getHeader: () => "Telefon",
        getValue: ({ employee }) => employee.phone || "",
        requiredPermissions: ["manage.employees.core_data"],
        requiredContext: ["employee"],
    },
    phone2: {
        getHeader: () => "Telefon 2",
        getValue: ({ employee }) => employee.phone2 || "",
        requiredPermissions: ["manage.employees.core_data"],
        requiredContext: ["employee"],
    },
    notes: {
        getHeader: () => "Notizen",
        getValue: ({ employee }) => employee.notes || "",
        requiredPermissions: ["manage.employees.core_data"],
        requiredContext: ["employee"],
    },
    birthday: {
        getHeader: () => "Geburtstag",
        getValue: ({ employee }) => (employee.birthday ? formatDate(employee.birthday) : ""),
        requiredPermissions: ["manage.employees.core_data"],
        requiredContext: ["employee"],
    },
    birthName: {
        getHeader: () => "Geburtsname",
        getValue: ({ employee }) => employee.birthName || "",
        requiredPermissions: ["manage.employees.core_data"],
        requiredContext: ["employee"],
    },
    birthCity: {
        getHeader: () => "Geburtsort",
        getValue: ({ employee }) => employee.birthCity || "",
        requiredPermissions: ["manage.employees.core_data"],
        requiredContext: ["employee"],
    },
    birthCountry: {
        getHeader: () => "Geburtsland",
        getValue: ({ employee }) => employee.birthCountry || "",
        requiredPermissions: ["manage.employees.core_data"],
        requiredContext: ["employee"],
    },
    nationality: {
        getHeader: () => "Nationalität",
        getValue: ({ employee }) => employee.nationality || "",
        requiredPermissions: ["manage.employees.core_data"],
        requiredContext: ["employee"],
    },
    gender: {
        getHeader: () => "Geschlecht",
        getValue: ({ employee }) => employee.gender || "",
        requiredPermissions: ["manage.employees.core_data"],
        requiredContext: ["employee"],
    },
    taxId: {
        getHeader: () => "Steuernummer",
        getValue: ({ employee }) => employee.taxId || "",
        requiredPermissions: ["manage.employees.core_data"],
        requiredContext: ["employee"],
    },
    socialSecurityNumber: {
        getHeader: () => "Sozialversicherungsnummer",
        getValue: ({ employee }) => employee.socialSecurityNumber || "",
        requiredPermissions: ["manage.employees.core_data"],
        requiredContext: ["employee"],
    },
    // contract fields
    employmentType: {
        getHeader: () => "Besch.verh.",
        getValue: ({ employee }) => {
            const employmentType = employee.currentContract?.employmentType;
            return employmentType != null ? employmentTypeLabel(employmentType) : "";
        },
        requiredPermissions: ["manage.employees.contract"],
        requiredContext: ["employee"],
    },
    nominalWeeklyHours: {
        getHeader: () => "Sollstunden (pro Woche)",
        getValue: ({ employee }) => {
            const nominalWeeklyHours = employee.currentContract?.nominalWeeklyHours;
            return nominalWeeklyHours != null ? formatNumber(nominalWeeklyHours) : "";
        },
        requiredPermissions: ["manage.employees.contract"],
        requiredContext: ["employee"],
    },
    vacationDays: {
        getHeader: () => "Urlaubsanspr. (Tage / Jahr)",
        getValue: ({ employee }) => {
            const vacationDays = employee.currentContract?.vacationDays;
            return vacationDays != null ? formatNumber(vacationDays) : "";
        },
        requiredPermissions: ["manage.employees.contract"],
        requiredContext: ["employee"],
    },
    monthlySalary: {
        getHeader: () => "Monatsgehalt",
        getValue: ({ employee }) => {
            const defaultSalary = employee.currentContract?.defaultSalary;
            if (defaultSalary?.type !== "monthly") {
                return "";
            }
            return `${formatNumber(defaultSalary.amount)} €`;
        },
        requiredPermissions: ["manage.employees.contract"],
        requiredContext: ["employee"],
    },
    hourlySalary: {
        getHeader: () => "Stundenlohn",
        getValue: ({ employee }) => {
            const defaultSalary = employee.currentContract?.defaultSalary;
            if (defaultSalary?.type !== "hourly") {
                return "";
            }
            return `${formatNumber(defaultSalary.amount)} €`;
        },
        requiredPermissions: ["manage.employees.contract"],
        requiredContext: ["employee"],
    },
    contract_end: {
        getHeader: () => "Vertragsende",
        getValue: ({ employee }) => {
            const latestEnd = employee.latestContract?.end;
            return latestEnd ? formatDate(latestEnd) : "Unbefristet";
        },
        requiredPermissions: ["manage.employees.contract"],
        requiredContext: ["employee"],
    },
    probationEnd: {
        getHeader: () => "Probezeitende",
        getValue: ({ employee }) => {
            const probationEnd = employee.currentContract?.probationInclusiveEnd;
            return probationEnd ? formatDate(probationEnd) : "";
        },
        requiredPermissions: ["manage.employees.contract"],
        requiredContext: ["employee"],
    },
    contract_start: {
        getHeader: () => "Vertragsbeginn",
        getValue: ({ employee }) => {
            const start = employee.currentContract?.start;
            return start ? formatDate(start) : "";
        },
        requiredPermissions: ["manage.employees.contract"],
        requiredContext: ["employee"],
    },
    employment_date: {
        getHeader: () => "Eintrittsdatum",
        getValue: ({ employee }) => {
            const start = employee.firstContract?.start;
            return start ? formatDate(start) : "";
        },
        requiredPermissions: ["manage.employees.contract"],
        requiredContext: ["employee"],
    },
    employedDays: {
        getHeader: () => "Betriebszugehörigkeit",
        getValue: ({ employee }) => {
            return formatEmployedInterval(employee.employedInterval);
        },
        requiredPermissions: ["manage.employees.contract"],
        requiredContext: ["employee"],
    },
    sfnAdvance: {
        getHeader: () => "SFN-Pauschale",
        getValue: ({ employee }) => {
            const { currentContract } = employee;
            if (!currentContract?.enableSFNLedger || !currentContract.sfnAdvance) {
                return "";
            }
            return `${formatNumber(currentContract.sfnAdvance)} € / Monat`;
        },
        requiredPermissions: ["manage.employees.contract"],
        requiredContext: ["employee"],
    },
    bonuses: {
        preCompile: ({ bonusTypes }) => {
            return bonusTypes.map((bonus) => ({
                type: "bonuses",
                disabled: false,
                bonusId: bonus.id,
            }));
        },
        getHeader: () => "Zuschläge",
        getSubHeader: ({ bonusTypes }, option) => {
            const bonus = bonusTypes.find((b) => b.id === option.bonusId);
            if (!bonus) {
                return "Unbenannter Zuschlag";
            }
            return bonus.name;
        },
        getValue: ({ employee }, option) => {
            const latestBoni = employee.latestContract?.bonuses || [];
            const bonus = latestBoni.find((bonus) => bonus.typeId === option.bonusId);
            if (!bonus) {
                return "";
            }
            return `${round(bonus.percent, 2)}%`;
        },
        requiredPermissions: ["manage.employees.contract"],
        requiredContext: ["employee"],
    },
    benefits: {
        preCompile: ({ benefitTypes }) => {
            return benefitTypes.map((benefit) => ({
                type: "benefits",
                disabled: false,
                benefitId: benefit.id,
            }));
        },
        getHeader: () => "Sonderleistungen",
        getSubHeader: ({ benefitTypes }, option) => {
            const benefit = benefitTypes.find((b) => b.id === option.benefitId);
            if (!benefit) {
                return "Unbenannte Sonderleistung";
            }
            return benefit.name;
        },
        getValue: ({ employee }, option) => {
            const latestBenefits = employee.latestContract?.benefits || [];
            const benefit = latestBenefits.find((benefit) => benefit.typeId === option.benefitId);
            if (!benefit) {
                return "";
            }
            return `${benefit.amount}€`;
        },
        requiredPermissions: ["manage.employees.contract"],
        requiredContext: ["employee"],
    },
    // Time report fields
    timeReportDays: {
        getHeader: () => "Arbeitszeit - Tage",
        getValue({ timeReportAggregate }) {
            return formatNumber(timeReportAggregate?.work.days || 0);
        },
        requiredPermissions: ["manage.employees.time"],
        requiredContext: ["timeReport"],
    },
    timeReportSundays: {
        getHeader: () => "Arbeitszeit - Sonntage",
        getValue({ timeReportAggregate }) {
            return formatNumber(timeReportAggregate?.work.sundays || 0);
        },
        requiredPermissions: ["manage.employees.time"],
        requiredContext: ["timeReport"],
    },
    timeReportHours: {
        getHeader: () => "Arbeitszeit - Stunden",
        getValue({ timeReportAggregate }) {
            return formatNumber(timeReportAggregate?.work.hours || 0);
        },
        requiredPermissions: ["manage.employees.time"],
        requiredContext: ["timeReport"],
    },
    vacationReportDays: {
        getHeader: () => "Urlaubszeit - Tage",
        getValue({ timeReportAggregate }) {
            return formatNumber(timeReportAggregate?.vacation.days || 0);
        },
        requiredPermissions: ["manage.employees.time"],
        requiredContext: ["timeReport"],
    },
    vacationReportHours: {
        getHeader: () => "Urlaubszeit - Stunden",
        getValue({ timeReportAggregate }) {
            return formatNumber(timeReportAggregate?.vacation.hours || 0);
        },
        requiredPermissions: ["manage.employees.time"],
        requiredContext: ["timeReport"],
    },
    sickReportDays: {
        getHeader: () => "Krankheit - Tage",
        getValue({ timeReportAggregate }) {
            return formatNumber(timeReportAggregate?.sick.days || 0);
        },
        requiredPermissions: ["manage.employees.time"],
        requiredContext: ["timeReport"],
    },
    sickReportHours: {
        getHeader: () => "Krankheit - Stunden",
        getValue({ timeReportAggregate }) {
            return formatNumber(timeReportAggregate?.sick.hours || 0);
        },
        requiredPermissions: ["manage.employees.time"],
    },
    // Employee time balance fields
    timeBalanceCarry: {
        getHeader: () => "Arbeitszeit Übertrag",
        getValue: ({ timeBalance }) => {
            const value = timeBalance?.reset || timeBalance?.carry || 0;
            return formatNumber(value);
        },
        requiredPermissions: ["manage.employees.ledgers_time"],
        requiredContext: ["timeBalance"],
    },
    timeBalanceNominal: {
        getHeader: () => "Arbeitszeit Soll",
        getValue: ({ timeBalance }) => {
            const value = timeBalance?.nominal || 0;
            return formatNumber(value);
        },
        requiredPermissions: ["manage.employees.ledgers_time"],
        requiredContext: ["timeBalance"],
    },
    timeBalanceActual: {
        getHeader: () => "Arbeitszeit Ist",
        getValue: ({ timeBalance }) => {
            const value = timeBalance?.actual || 0;
            return formatNumber(value);
        },
        requiredPermissions: ["manage.employees.ledgers_time"],
        requiredContext: ["timeBalance"],
    },
    timeBalanceDifference: {
        getHeader: () => "Arbeitszeit Differenz",
        getValue: ({ timeBalance }) => {
            const value = timeBalance?.difference || 0;
            return formatNumber(value);
        },
        requiredPermissions: ["manage.employees.ledgers_time"],
        requiredContext: ["timeBalance"],
    },
    timeBalanceBalance: {
        getHeader: () => "Arbeitszeit Saldo",
        getValue: ({ timeBalance }) => {
            const value = timeBalance?.balance || 0;
            return formatNumber(value);
        },
        requiredPermissions: ["manage.employees.ledgers_time"],
        requiredContext: ["timeBalance"],
    },
    vacationBalanceCarry: {
        getHeader: () => "Urlaub Übertrag",
        getValue: ({ vacationBalance }) => {
            const value = vacationBalance?.reset || vacationBalance?.carry || 0;
            return formatNumber(value);
        },
        requiredPermissions: ["manage.employees.ledgers_vacation"],
        requiredContext: ["vacationBalance"],
    },
    vacationBalanceNominal: {
        getHeader: () => "Urlaub Zuwachs",
        getValue: ({ vacationBalance }) => {
            const value = vacationBalance?.nominal || 0;
            return formatNumber(value);
        },
        requiredPermissions: ["manage.employees.ledgers_vacation"],
        requiredContext: ["vacationBalance"],
    },
    vacationBalanceActual: {
        getHeader: () => "Urlaub Genommen",
        getValue: ({ vacationBalance }) => {
            const value = vacationBalance?.actual || 0;
            return formatNumber(value);
        },
        requiredPermissions: ["manage.employees.ledgers_vacation"],
        requiredContext: ["vacationBalance"],
    },
    vacationBalanceDifference: {
        getHeader: () => "Urlaub Differenz",
        getValue: ({ vacationBalance }) => {
            const value = vacationBalance?.difference || 0;
            return formatNumber(value);
        },
        requiredPermissions: ["manage.employees.ledgers_vacation"],
        requiredContext: ["vacationBalance"],
    },
    vacationBalanceBalance: {
        getHeader: () => "Urlaub Saldo",
        getValue: ({ vacationBalance }) => {
            const value = vacationBalance?.balance || 0;
            return formatNumber(value);
        },
        requiredPermissions: ["manage.employees.ledgers_vacation"],
        requiredContext: ["vacationBalance"],
    },
    bonusesBalanceCarry: {
        getHeader: () => "Bonus Übertrag",
        getValue: ({ bonusesBalance }) => {
            const value = bonusesBalance?.reset || bonusesBalance?.carry;
            return value != null ? formatNumber(value) : "N/A";
        },
        requiredPermissions: ["manage.employees.ledgers_bonuses"],
        requiredContext: ["bonusesBalance"],
    },
    bonusesBalanceNominal: {
        getHeader: () => "Bonus Pauschale",
        getValue: ({ bonusesBalance }) => {
            const value = bonusesBalance?.nominal;
            return value != null ? formatNumber(value) : "N/A";
        },
        requiredPermissions: ["manage.employees.ledgers_bonuses"],
        requiredContext: ["bonusesBalance"],
    },
    bonusesBalanceActual: {
        getHeader: () => "Bonus Verdient",
        getValue: ({ bonusesBalance }) => {
            const value = bonusesBalance?.actual || 0;
            return formatNumber(value);
        },
        requiredPermissions: ["manage.employees.ledgers_bonuses"],
        requiredContext: ["bonusesBalance"],
    },
    bonusesBalanceDifference: {
        getHeader: () => "Bonus Differenz",
        getValue: ({ bonusesBalance }) => {
            const value = bonusesBalance?.difference;
            return value != null ? formatNumber(value) : "N/A";
        },
        requiredPermissions: ["manage.employees.ledgers_bonuses"],
        requiredContext: ["bonusesBalance"],
    },
    bonusesBalanceBalance: {
        getHeader: () => "Bonus Saldo",
        getValue: ({ bonusesBalance }) => {
            const value = bonusesBalance?.balance;
            return value != null ? formatNumber(value) : "N/A";
        },
        requiredPermissions: ["manage.employees.ledgers_bonuses"],
        requiredContext: ["bonusesBalance"],
    },
};
//# sourceMappingURL=config.js.map